import { useContext, useState } from "react";
import { Button } from "@headlessui/react";

import { CollectionMenuItem as CollectionMenuItemType } from "../HeaderMenuTypes";
import clsx from "clsx";
import BaseMenuItem from "./BaseMenuItem";
import { HeaderMenuContext } from "../HeaderMenuContext";
import { ShopifyObservedImage } from "../../../components/ShopifyObservedImage";
import { Product } from "../../ShopifyTypes";

interface CollectionMenuItemProps {
  item: CollectionMenuItemType;
}

export default function CollectionMenuItem({
  item: {
    collectionHandles,
    buttonText,
    buttonCollectionHandle,
    ...baseMenuItem
  },
}: CollectionMenuItemProps) {
  const [activeCollectionHandle, setActiveCollectionHandle] = useState<string>(
    collectionHandles[0]
  );
  const { headerCollections } = useContext(HeaderMenuContext);
  const { collection: buttonCollection } = headerCollections.find(
    ({ collection: { handle } }) => handle === buttonCollectionHandle
  )!;

  return (
    <BaseMenuItem
      {...baseMenuItem}
      subMenuContent={
        <>
          <div className="xflex xflex-col xjustify-between xitems-start xgap-4 xw-1/3">
            <div className="xflex xflex-col xgap-4">
              {collectionHandles.map((collectionHandle) => (
                <CollectionSubMenuButton
                  key={collectionHandle}
                  active={activeCollectionHandle === collectionHandle}
                  collectionHandle={collectionHandle}
                  onMouseEnter={() =>
                    setActiveCollectionHandle(collectionHandle)
                  }
                />
              ))}
            </div>
            <Button
              className="xbtn xbtn-primary xbtn-lg"
              onClick={() => {
                const url = `/collections/${buttonCollection.handle}`;
                window.location.href = url;
              }}
            >
              {buttonText}
            </Button>
          </div>
          <CollectionSubMenu collectionHandle={activeCollectionHandle} />
        </>
      }
    />
  );
}
interface CollectionSubMenuButtonProps {
  active: boolean;
  collectionHandle: string;
  onMouseEnter?: () => void;
}

function CollectionSubMenuButton({
  active,
  collectionHandle,
  onMouseEnter,
}: CollectionSubMenuButtonProps) {
  const { headerCollections } = useContext(HeaderMenuContext);

  const { collection } = headerCollections.find(
    ({ collection: { handle } }) => handle === collectionHandle
  )!;

  return (
    <h2
      className={clsx(
        "xfont-grotesque",
        "xfont-bold",
        "xtext-6xl",
        "xcursor-pointer",
        active ? "xtext-orange" : "xtext-extra-dark-green"
      )}
      onMouseEnter={onMouseEnter}
    >
      {collection.title}
    </h2>
  );
}

interface CollectionSubMenuProps {
  collectionHandle: string;
}

function CollectionSubMenu({ collectionHandle }: CollectionSubMenuProps) {
  const { headerCollections } = useContext(HeaderMenuContext);

  const { products } = headerCollections.find(
    ({ collection: { handle } }) => handle === collectionHandle
  )!;

  return (
    <div className="xflex xgap-12 xw-2/3 xjustify-start">
      {products.slice(0, 4).map((product) => (
        <CollectionSubMenuCard
          key={`${collectionHandle}-${product.handle}`}
          product={product}
        />
      ))}
    </div>
  );
}

interface CollectionSubMenuCardProps {
  product: Product;
}

function CollectionSubMenuCard({
  product: { featured_image, title, ...product },
}: CollectionSubMenuCardProps) {
  return (
    <div
      className="
        xflex xflex-col
        xgap-4 xitems-center
        xcursor-pointer
      "
      onClick={(e) => {
        const url = `/products/${product.handle}`;

        e.preventDefault();
        window.location.href = url;
      }}
    >
      <ShopifyObservedImage
        className="
          xsize-80 xobject-cover
          xrounded-3xl xshadow-lg
        "
        src={featured_image}
        fallbackWidth={320}
        fallbackHeight={320}
        shopify={{ crop: "center" }}
        loading="lazy"
        alt="green-logo"
        width={320}
        height={320}
      />
      <h3
        className="
          xfont-grotesque
          xtext-3xl xfont-semibold
          xcolor-extra-dark-green
        "
      >
        {title}
      </h3>
    </div>
  );
}
